import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, ShieldCheck, ExternalLink, Copy, Share2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Digital ID card — shown to the currently signed-in employee/partner.
 * The QR image is a base64 PNG returned by the backend and encodes the
 * publicly verifiable URL /verify/{code}.
 */
export default function MyDigitalId() {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/digital-ids/me");
        setCard(r.data);
      } catch (e) {
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error(e?.response?.data?.detail || "Failed to load Digital ID");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = () => {
    if (!card?.qr_data_url) return;
    const a = document.createElement("a");
    a.href = card.qr_data_url;
    a.download = `${card.code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) {
    return <AppLayout><div className="p-8">Loading…</div></AppLayout>;
  }

  if (notFound) {
    return (
      <AppLayout>
        <div className="p-6 max-w-xl mx-auto text-center" data-testid="my-id-empty">
          <div className="bg-white border rounded-xl p-8">
            <ShieldCheck className="mx-auto text-slate-400 mb-3" size={40}/>
            <h1 className="font-display font-black text-xl">No Digital ID yet</h1>
            <p className="text-sm text-[var(--miro-text-muted)] mt-2">
              Your Digital ID is auto-issued after an admin approves your MIRO One access request
              (employees) or partner application (partners).
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const badgeColor = card.holder_type === "partner"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-sky-100 text-sky-800";

  return (
    <AppLayout>
      <div className="p-6" data-testid="my-id-page">
        <div className="mb-6">
          <div className="miro-overline">Identity</div>
          <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
            My Digital ID
          </h1>
          <p className="text-sm text-[var(--miro-text-muted)] mt-1">
            Anyone can verify this card by scanning the QR or opening the verification link.
          </p>
        </div>

        <div className="max-w-2xl">
          <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-xl bg-gradient-to-br from-[#0b1533] via-[#0f1f4d] to-[#153a75] text-white p-6 relative"
               data-testid="digital-id-card">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                 style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "12px 12px" }}/>
            <div className="flex justify-between items-start gap-6 relative">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">{card.org}</div>
                <div className="font-display font-black text-2xl mt-1 truncate">{card.holder_name}</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${badgeColor}`}>
                    {card.holder_type === "partner"
                      ? `${(card.partner_type || "").toUpperCase()} PARTNER`
                      : "EMPLOYEE"}
                  </span>
                  <span className="text-xs opacity-80">{card.holder_role?.replace(/_/g, " ")}</span>
                </div>
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-widest opacity-60">Identity code</div>
                  <div className="font-mono text-lg font-bold" data-testid="digital-id-code">{card.code}</div>
                </div>
                <div className="mt-3 text-xs opacity-80">
                  Status: <b className={card.status === "active" ? "text-emerald-300" : "text-amber-300"}>{card.status}</b>
                  {card.issued_at && <> · Issued {new Date(card.issued_at).toLocaleDateString()}</>}
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 shrink-0" data-testid="digital-id-qr">
                <img src={card.qr_data_url} alt={`QR for ${card.code}`} className="w-32 h-32"/>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={download} data-testid="download-qr-btn">
              <Download size={14} className="mr-1"/> Download QR
            </Button>
            <Button asChild variant="outline" data-testid="open-verify-btn">
              <a href={card.verification_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} className="mr-1"/> Open verification page
              </a>
            </Button>
          </div>

          {/* Personal referral link — every lead submitted through this URL is
              auto-tagged to this Digital ID holder so admins can see who
              referred it (and franchise partners still earn commission). */}
          <div className="mt-6 rounded-xl border border-[var(--miro-border)] bg-white p-5"
               data-testid="my-referral-link-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Share2 size={18}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="miro-overline text-[var(--miro-text-muted)]">
                  Your personal referral link
                </div>
                <div className="font-display font-bold text-[var(--miro-navy)] mt-1">
                  Share this to attribute every lead to you
                </div>
                <div className="text-xs text-[var(--miro-text-muted)] mt-1">
                  Anyone who submits the public form via this URL will be
                  automatically tagged with your code
                  <span className="font-mono ml-1 text-[var(--miro-blue-600)]">{card.code}</span>.
                  Internal team sees the referrer instantly on the lead card.
                </div>
                {(() => {
                  const link = `${window.location.origin}/leadform?ref=${encodeURIComponent(card.code)}`;
                  return (
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        readOnly
                        data-testid="my-referral-link-input"
                        value={link}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 h-9 rounded-md border border-[var(--miro-border)] bg-[var(--miro-bg)] px-3 text-xs font-mono text-[var(--miro-navy)]"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="my-referral-copy-btn"
                        onClick={() => {
                          navigator.clipboard?.writeText(link);
                          toast.success("Referral link copied");
                        }}
                      >
                        <Copy size={13} className="mr-1"/> Copy
                      </Button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <p className="text-xs text-[var(--miro-text-muted)] mt-6">
            Signed in as <b>{user?.email}</b>. If your name or role is wrong, contact HR.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
